import { useState } from "react";
import type React from "react";
import {
  useQuery,
  getTradovateConnections,
  getPropFirmTemplates,
  assignTradovateConnectionPropFirm,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Plus, Link2, Loader2, Lock, ChevronRight, FlaskConical } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { Card } from "../../client/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import { cn } from "../../client/utils";
import { userHasBrokerSync } from "../../payment/plans";
import TradovateSyncStatus from "./TradovateSyncStatus";
import TradovateConnectionBeamCard from "./TradovateConnectionBeamCard";
import TradovateDemoSyncStatus from "./TradovateDemoSyncStatus";
import { resolveSyncPresentation } from "./tradovateSyncPresentation";
import { resolvePropFirmLogo } from "./tradovatePropFirmLogo";
import { filterTradovatePropFirms } from "./tradovatePropFirms";
import { DEMO_CONNECTIONS, isSyncDemoEnabled } from "./tradovateSyncDemoData";

interface Props {
  /** Opens the OAuth wizard to connect an ADDITIONAL prop firm. */
  onConnectNewPropFirm: () => void;
  /** Opens the import review to add another account WITHIN one connection. */
  onAddAccountToConnection: (connectionId: string) => void;
  /**
   * Comptes enrichis de la page (allAccountsFlat). Chacun porte
   * `tradovateSyncedAccount.connectionId`, ce qui permet de rattacher un compte
   * à sa connexion sans requête supplémentaire.
   */
  accounts?: any[];
  /**
   * Rend les lignes de comptes d'une connexion dépliée. Injecté par la page
   * pour réutiliser AccountTable : les lignes dépliées sont ainsi identiques à
   * celles du tableau principal, actions comprises.
   */
  renderAccounts?: (accountsForConnection: any[]) => React.ReactNode;
  /**
   * Mode d'affichage de la page (`edgely:accounts-view`).
   *
   * En `cards`, la section rend une card par prop firm avec l'animation de
   * faisceaux ; en `table` elle garde la liste de lignes historique, INCHANGEE.
   * Defaut `table` : un appelant qui ne passe rien conserve l'ancien rendu.
   */
  view?: "cards" | "table";
}

/**
 * Card conteneur « Synchronisation Tradovate » avec une mini-card par prop firm
 * (une connexion = une prop firm = un login OAuth).
 *
 * L'entête porte l'action « connecter UNE AUTRE prop firm », délibérément
 * séparée du « ajouter un compte à CETTE prop firm » de chaque mini-card :
 * confondre les deux est ce qui faisait croire que la synchro multi-prop-firm
 * était impossible.
 */
export default function TradovateConnectionsSection({
  onConnectNewPropFirm,
  onAddAccountToConnection,
  accounts = [],
  renderAccounts,
  view = "table",
}: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: user } = useAuth();
  const { data: connections = [], refetch } = useQuery(getTradovateConnections, {});
  const { data: templates = [] } = useQuery(getPropFirmTemplates);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ── MODE DEMO ────────────────────────────────────────────────────────────
  // Deux conditions CUMULATIVES (cf. `isSyncDemoEnabled`) : le parametre d'URL
  // ET un build de developpement. `import.meta.env.DEV` est fige a la
  // compilation, donc l'URL seule ne peut rien declencher en production.
  const isDemo = isSyncDemoEnabled(
    typeof window === "undefined" ? "" : window.location.search,
    import.meta.env.DEV,
  );

  const conns = isDemo ? (DEMO_CONNECTIONS as any[]) : (connections as any[]);
  // La synchro broker est l'avantage du palier Max. Un utilisateur rétrogradé
  // peut conserver des connexions existantes : on affiche alors ses cards mais
  // sans l'action de connexion, qui partirait en 403 côté serveur.
  const canConnect = !!user && userHasBrokerSync(user as any);

  if (conns.length === 0) return null;

  const handleAssign = async (connectionId: string, propFirmTemplateId: string) => {
    // ⚠️ AUCUNE MUTATION EN DEMO. Les connexions simulees n'existent pas cote
    // serveur : l'appel partirait en 404 et, pire, donnerait a croire que
    // l'ecran agit sur des donnees reelles.
    if (isDemo) return;
    setAssigningId(connectionId);
    try {
      await assignTradovateConnectionPropFirm({ connectionId, propFirmTemplateId });
      await refetch();
    } catch (err: any) {
      toast({
        title: t.tradovate.errorGeneric,
        description: err?.message || undefined,
        variant: "destructive",
      });
    } finally {
      setAssigningId(null);
    }
  };

  /** Ajout de compte : neutralise en demo, comme toute action reseau. */
  const handleAddAccount = (connectionId: string) => {
    if (isDemo) return;
    onAddAccountToConnection(connectionId);
  };

  /**
   * Selecteur de rattachement a une prop firm, pour les connexions qui n'en ont
   * pas encore. Partage par les deux vues : la regle « pas de template, donc on
   * propose de choisir » ne doit exister qu'une fois.
   */
  const renderAssignSelect = (connId: string) => (
    <div className="flex items-center gap-1.5" title={t.tradovate.assignPropFirmHint}>
      {assigningId === connId ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : (
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
      )}
      <Select
        disabled={assigningId !== null}
        onValueChange={(value) => handleAssign(connId, value)}
      >
        <SelectTrigger className="h-8 w-[180px] text-xs">
          <SelectValue placeholder={t.tradovate.assignPropFirm} />
        </SelectTrigger>
        <SelectContent>
          {filterTradovatePropFirms(templates as any[]).map((tpl: any) => (
            <SelectItem key={tpl.id} value={tpl.id} className="text-xs">
              {tpl.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Card className="mb-6 p-4">
      {/* Entête du conteneur */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">{t.tradovate.sectionTitle}</h2>
          <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
            {t.tradovate.sectionFirmCount.replace("{count}", String(conns.length))}
          </Badge>
        </div>
        {canConnect ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={isDemo ? undefined : onConnectNewPropFirm}
            disabled={isDemo}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t.tradovate.connectNewPropFirm}
          </Button>
        ) : (
          // Plan sans synchro broker : on explique au lieu de laisser cliquer
          // vers un 403 « limite atteinte » qui n'est pas la vraie raison.
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            {t.tradovate.maxPlanRequired}
          </span>
        )}
      </div>

      {/* BANDEAU DE DEMONSTRATION — volontairement voyant et en tete : une
          capture d'ecran de cette section ne doit jamais pouvoir etre prise
          pour un etat reel du compte. */}
      {isDemo && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-3 py-2">
          <FlaskConical className="h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
          <span className="text-xs font-semibold text-[hsl(var(--warning))]">
            {t.tradovate.demoBannerTitle}
          </span>
          <span className="text-xs text-muted-foreground">{t.tradovate.demoBannerHint}</span>
        </div>
      )}

      {view === "cards" ? (
        // ── VUE CARDS — une card par prop firm, animation de faisceaux ─────
        //
        // NOMBRE DE COLONNES PILOTE PAR LA LARGEUR DE CARD, pas par l'ecran.
        //
        // L'ancienne grille (`md:grid-cols-2 xl:grid-cols-3`) lisait la largeur
        // de la FENETRE, alors que la section vit a cote de la sidebar : a
        // 1280 px de fenetre, trois cards de ~380 px ecrasaient le pied (badge
        // d'etat coupe en « Sy / en », fraicheur sur deux lignes) et
        // debordaient la colonne « aucun compte ». `auto-fill` + plancher de
        // 420 px ne cree une colonne que si la place REELLE la permet ; `min()`
        // laisse la card descendre sous le plancher quand une seule tient.
        //
        // PLAFOND DE 4 PAR LIGNE : sur grand ecran, cinq cards a 420 px se
        // touchaient et l'animation de faisceaux n'avait plus la place d'etre
        // lue. Le `max()` releve le plancher au quart de la ligne (moins les
        // trois gouttieres de `gap-3` = 2.25rem), ce qui interdit une 5e piste.
        <div className="grid grid-cols-[repeat(auto-fill,minmax(max(min(100%,420px),calc((100%_-_2.25rem)/4)),1fr))] gap-3">
          {conns.map((conn: any) => {
            const firm = conn.propFirmTemplate;
            const logo = resolvePropFirmLogo(
              firm ? { slug: firm.slug, logoUrl: firm.logoUrl, color: firm.color } : null,
            );
            const firmName = firm?.name ?? t.tradovate.connectionBannerTitle;
            // En demo les comptes viennent du jeu simule ; sinon du meme
            // rattachement que la vue tableau.
            const beamAccounts = isDemo
              ? (conn.demoAccounts as any[]).map((a: any) => ({ id: a.id, label: a.name }))
              : (accounts as any[])
                  .filter((a: any) => a?.tradovateSyncedAccount?.connectionId === conn.id)
                  .map((a: any) => ({
                    id: String(a.id),
                    // On prefere l'identifiant BROKER (« APEX5455610000009 »)
                    // au nom local : c'est lui qui figure chez la prop firm,
                    // donc celui que l'utilisateur reconnait. `Account.name`
                    // reste le repli, un compte pouvant avoir ete renomme.
                    label:
                      a.tradovateSyncedAccount?.tradovateAccountName ||
                      a.name ||
                      String(a.id),
                  }));

            // ── ETAT DE LA CONNEXION, POUR LA LUMIERE DU FAISCEAU ──────────
            //
            // La card doit savoir si la connexion collecte REELLEMENT : sur une
            // connexion morte (« Reconnexion requise »), animer un flux
            // affirmerait des donnees qui ne circulent pas.
            //
            // ⚠️ CE N'EST PAS UN DOUBLON DE `TradovateSyncStatus`, qui resout
            // la meme presentation de son cote. Ce composant recoit un
            // `connectionId` et refait sa propre requete : son verdict lui
            // reste INTERNE, et il ne rend qu'un `ReactNode` opaque que la card
            // ne peut pas interroger. On appelle donc la MEME regle pure ici,
            // sur les memes deux champs — la duplication serait de recoder la
            // detection, pas de rappeler la fonction qui la porte.
            //
            // Les deux champs sont servis par `getTradovateConnections` comme
            // par le jeu de demonstration : un seul chemin de calcul couvre
            // donc les deux modes, et la card de demo en `needs_reauth`
            // (Topstep) montre l'extinction a cote des cards vivantes.
            const syncTone = resolveSyncPresentation({
              displayStatus: conn.displayStatus,
              realtimeStatus: conn.realtimeStatus,
            }).tone;

            return (
              <TradovateConnectionBeamCard
                key={conn.id}
                firmName={firmName}
                logoSrc={logo.src}
                logoColor={logo.color}
                // Deja present dans le `select` de `getTradovateConnections` :
                // la valeur arrivait jusqu'ici sans que rien ne l'affiche.
                environment={conn.environment}
                accounts={beamAccounts}
                onAddAccount={() => handleAddAccount(conn.id)}
                syncTone={syncTone}
                footerStatus={
                  isDemo ? (
                    <TradovateDemoSyncStatus
                      displayStatus={conn.displayStatus}
                      realtimeStatus={conn.realtimeStatus}
                      lastSyncAt={conn.lastSyncAt}
                      part="badge"
                    />
                  ) : (
                    <TradovateSyncStatus connectionId={conn.id} part="badge" />
                  )
                }
                footerActions={
                  isDemo ? (
                    <TradovateDemoSyncStatus
                      displayStatus={conn.displayStatus}
                      realtimeStatus={conn.realtimeStatus}
                      lastSyncAt={conn.lastSyncAt}
                      part="actions"
                    />
                  ) : (
                    <TradovateSyncStatus connectionId={conn.id} part="actions" />
                  )
                }
                assignSlot={!firm ? renderAssignSelect(conn.id) : undefined}
              />
            );
          })}
        </div>
      ) : (
      /* ── LES COLONNES SONT DEFINIES ICI, PAS DANS CHAQUE LIGNE ───────────
          Chaque ligne portait sa propre grille, et une grille n'aligne ses
          colonnes qu'EN SON SEIN : trois grilles independantes donnaient trois
          jeux de largeurs. Mesure avant correction, abscisse des colonnes 2
          et 3 : 1033/999/1033 px et 1341/1374/1406 px -- seule la colonne 1
          tombait juste.

          Le `grid` vit donc sur le CONTENEUR et les lignes s'y raccrochent en
          `subgrid`, ce qui leur fait heriter des memes pistes. `gap-y-2`
          remplace l'ancien `space-y-2`, qui n'a aucun effet entre des
          elements de grille. */
      <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
        {conns.map((conn: any) => {
          const firm = conn.propFirmTemplate;
          const logo = resolvePropFirmLogo(
            firm ? { slug: firm.slug, logoUrl: firm.logoUrl, color: firm.color } : null,
          );
          const firmName = firm?.name ?? t.tradovate.connectionBannerTitle;
          // Les comptes de CETTE connexion, retrouvés via le lien porté par
          // chaque compte enrichi. On compte les comptes réellement présents
          // dans la page plutôt que syncedAccounts, pour que le total affiché
          // corresponde exactement aux lignes dépliées.
          const firmAccounts = (accounts as any[]).filter(
            (a: any) => a?.tradovateSyncedAccount?.connectionId === conn.id,
          );
          // Le compte AFFICHÉ vient du serveur (`activeSyncedAccountCount`), qui
          // joint sur `Account.isActive`.
          //
          // NE PAS revenir à `firmAccounts.length || syncedAccounts.length` : ce
          // `||` confondait « 0 compte actif » et « page pas encore chargée »,
          // deux états indistinguables ici. Résultat, une prop firm dont TOUS les
          // comptes étaient cramés retombait sur le nombre de liens broker, qui
          // ne décroît jamais : la ligne annonçait « 4 comptes synchronisés »
          // juste sous un badge « aucun compte actif », et sans chevron puisque
          // `isExpandable` voyait bien 0 ligne à déplier.
          //
          // `firmAccounts.length` reste le repli quand le serveur ne renvoie pas
          // encore le champ (connexion mise en cache par une version antérieure).
          const accountCount = conn.activeSyncedAccountCount ?? firmAccounts.length;
          const isExpandable = !!renderAccounts && firmAccounts.length > 0;
          const isExpanded = expandedId === conn.id;

          return (
            <div
              key={conn.id}
              className="rounded-lg border border-border bg-muted/30 sm:col-span-4 sm:grid sm:grid-cols-subgrid"
            >
            {/* ── GRILLE A COLONNES FIXES, ET NON UN FLEX LIBRE ──────────────
                Chaque ligne posait ses colonnes bout a bout : le badge
                demarrait apres le nom, donc a une abscisse differente selon la
                longueur du nom (« Lucid Trading » vs « ApexTraderFunding »), et
                la date apres un badge dont la largeur varie du simple au triple
                (« Synchronise » vs « Sync active, aucun compte actif »). Rien
                ne s'alignait d'une ligne a l'autre.

                Les colonnes sont donc declarees au niveau de la LIGNE, pas
                remplies par leur contenu. Les largeurs sont en `minmax(...)` :
                les badges gardent une colonne stable sans etre tronques si une
                traduction s'allonge.

                Sous `sm`, la grille retombe en pile : a cette largeur une
                grille a 5 colonnes produirait des colonnes illisibles. */}
            <div className="grid grid-cols-1 items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:col-span-4 sm:grid-cols-subgrid sm:gap-x-4">
              <div
                className={cn(
                  "flex min-w-0 items-center gap-2.5",
                  isExpandable && "cursor-pointer select-none",
                )}
                onClick={isExpandable ? () => setExpandedId(isExpanded ? null : conn.id) : undefined}
                role={isExpandable ? "button" : undefined}
                tabIndex={isExpandable ? 0 : undefined}
                aria-expanded={isExpandable ? isExpanded : undefined}
                onKeyDown={
                  isExpandable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpandedId(isExpanded ? null : conn.id);
                        }
                      }
                    : undefined
                }
              >
                {/* ⚠️ GOUTTIERE TOUJOURS RESERVEE, chevron ou pas.
                    Rendu conditionnellement, il disparaissait sur les
                    connexions sans compte depliable (Lucid Trading, 0 compte
                    actif) et TOUTE la ligne glissait de ~26 px vers la gauche :
                    logo, nom et badge desalignes par rapport aux lignes
                    voisines. On reserve la place, on masque l'icone. */}
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {isExpandable && (
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        isExpanded && "rotate-90",
                      )}
                    />
                  )}
                </span>
                {/* Surface claire neutre : plusieurs logos de prop firms sont
                    sombres et disparaîtraient sur les thèmes dark/jungle. */}
                {logo.src ? (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-white/90 p-1">
                    <img src={logo.src} alt="" className="max-h-full max-w-full object-contain" />
                  </span>
                ) : (
                  // La couleur vient de la BDD (badge de la prop firm) ; sans
                  // couleur on retombe sur un token de thème, jamais un hex en dur.
                  <span
                    className={cn(
                      "inline-block h-3 w-3 shrink-0 rounded-full",
                      !logo.color && "bg-muted-foreground",
                    )}
                    style={logo.color ? { backgroundColor: logo.color } : undefined}
                  />
                )}
                {/* Colonne 1 — IDENTITE SEULE. Le badge de statut a quitte ce
                    bloc : colle au nom, il heritait de sa largeur variable et
                    ne pouvait pas s'aligner. Il a desormais sa propre colonne. */}
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{firmName}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.tradovate.firmAccountCount.replace("{count}", String(accountCount))}
                  </div>
                </div>
              </div>

              {/* Colonnes 2 et 3 — STATUT puis FRAICHEUR. Monte SANS conteneur
                  intermediaire : le composant se raccroche directement aux
                  pistes de la ligne (`col-span-2` + `subgrid`), sans quoi le
                  badge et la date seraient enfermes dans une seule colonne et
                  se decaleraient a nouveau. */}
              {isDemo ? (
                <TradovateDemoSyncStatus
                  displayStatus={conn.displayStatus}
                  realtimeStatus={conn.realtimeStatus}
                  lastSyncAt={conn.lastSyncAt}
                  part="badge"
                />
              ) : (
                <TradovateSyncStatus connectionId={conn.id} part="badge" />
              )}

              {/* Colonne 3 — ACTIONS, calees a droite. `justify-end` les colle
                  au bord : les boutons « Ajouter un compte X » portent des
                  libelles de longueurs differentes, seul leur bord DROIT peut
                  s'aligner. */}
              <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                {!firm && renderAssignSelect(conn.id)}
                {isDemo ? (
                  <TradovateDemoSyncStatus
                    displayStatus={conn.displayStatus}
                    realtimeStatus={conn.realtimeStatus}
                    lastSyncAt={conn.lastSyncAt}
                    part="actions"
                  />
                ) : (
                  <TradovateSyncStatus connectionId={conn.id} part="actions" />
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0"
                  onClick={() => handleAddAccount(conn.id)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  {firm
                    ? t.tradovate.addAccountForFirm.replace("{firm}", firm.name)
                    : t.tradovate.addAccount}
                </Button>
              </div>
            </div>

            {/* Comptes de cette prop firm, rendus par AccountTable (injecté par
                la page) : mêmes lignes et mêmes actions que le tableau du bas. */}
            {isExpandable && isExpanded && (
              <div className="border-t border-border/60 px-1 pb-1 sm:col-span-4">
                {renderAccounts!(firmAccounts)}
              </div>
            )}
            </div>
          );
        })}
      </div>
      )}
    </Card>
  );
}
